import React, { useState, useEffect, useCallback } from 'react';
import { useJobs } from '../hooks/useJobs';
import { Job } from '../types';
import { AlertTriangle, CheckCircle, Clock, ArrowLeft } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@cleanpro/common-ui';

// Form data interface matching database structure
interface JobFormData {
  customer_id: string;
  service_id: string;
  business_id: string;
  property_id?: string | null;
  quote_id?: string | null;
  date_time: string;
  address: string;
  notes: string;
  price: number;
  duration: number;
  status: string;
  is_recurring: boolean;
  recurring_frequency?: 'weekly' | 'bi-weekly' | 'tri-weekly' | 'monthly';
  recurring_end_date?: string;
}

interface CreateJobProps {
  onSubmit?: (jobData: Omit<Job, 'id'>) => void;
  onCancel?: () => void;
  initialData?: Partial<Job & { id?: string }>;
}

export default function CreateJob(props: Readonly<CreateJobProps>) {
  const { onSubmit, onCancel, initialData } = props;
  const { customers, services, businesses, validateJobScheduling, loading } = useJobs();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const quoteId = searchParams.get('quoteId');

  const [formData, setFormData] = useState<Partial<JobFormData>>({
    customer_id: '',
    service_id: '',
    business_id: '',
    date_time: '',
    address: '',
    notes: '',
    price: 0,
    duration: 0,
    status: 'requested',
    is_recurring: false,
    recurring_frequency: 'weekly',
    recurring_end_date: '',
  });

  // Separate date and time state for form inputs
  const [dateTime, setDateTime] = useState({
    date: '',
    time: ''
  });

  // Initialize date/time from date_time when formData changes
  useEffect(() => {
    if (formData.date_time && formData.date_time !== '') {
      const dateTimeValue = new Date(formData.date_time);
      if (!Number.isNaN(dateTimeValue.getTime())) {
        setDateTime({
          date: dateTimeValue.toISOString().split('T')[0],
          time: dateTimeValue.toTimeString().slice(0, 5)
        });
      }
    }
  }, [formData.date_time]);

  const [validation, setValidation] = useState<{
    isValid: boolean;
    conflicts: string[];
    suggestions: string[];
  } | null>(null);

  const [isValidating, setIsValidating] = useState(false);

  // Validate scheduling when key fields change
  const validateScheduling = useCallback(async () => {
    if (!formData.business_id || !dateTime.date || !dateTime.time || !formData.service_id) {
      return;
    }

    setIsValidating(true);
    try {
      const service = services.find(s => s.id === formData.service_id);
      if (!service) return;

      const result = await validateJobScheduling(
        formData.business_id,
        dateTime.date,
        dateTime.time,
        service.duration,
        initialData?.id // Exclude current job if editing
      );

      setValidation(result);
    } catch (err) {
      console.error('Validation error:', err);
      setValidation({
        isValid: false,
        conflicts: ['Unable to validate schedule'],
        suggestions: []
      });
    } finally {
      setIsValidating(false);
    }
  }, [formData.business_id, dateTime.date, dateTime.time, formData.service_id, services, validateJobScheduling, initialData?.id]);

  useEffect(() => {
    if (quoteId) {
      console.log('Loading quote data for quoteId:', quoteId);
      // Fetch quote data and pre-populate form
      const fetchQuote = async () => {
        try {
          const { data, error } = await supabase
            .from('quotes')
            .select(`
              *,
              customers(id, name),
              requests(id, service_id, notes, requested_date_time),
              businesses(id, business_name)
            `)
            .eq('id', quoteId)
            .single();

          if (error) throw error;

          console.log('Fetched quote data:', data);

          // Extract service ID from quote (now has service_id column) or request as fallback
          const serviceId = data.service_id || data.requests?.service_id || null;

          // Pre-populate formData with quote details
          setFormData(prev => ({
            ...prev,
            quote_id: data.id,
            customer_id: data.customer_id || prev.customer_id,
            service_id: serviceId || prev.service_id,
            business_id: data.business_id || prev.business_id,
            notes: data.requests?.notes || prev.notes,
            duration: prev.duration, // Keep existing duration for now
            is_recurring: prev.is_recurring, // Keep existing recurring settings
            recurring_frequency: prev.recurring_frequency,
            recurring_end_date: prev.recurring_end_date
          }));

          // Parse the requested_date_time for the form inputs
          if (data.requests?.requested_date_time) {
            const dateTimeValue = new Date(data.requests.requested_date_time);
            if (!Number.isNaN(dateTimeValue.getTime())) {
              setDateTime({
                date: dateTimeValue.toISOString().split('T')[0],
                time: dateTimeValue.toTimeString().slice(0, 5)
              });
            }
          }
        } catch (err) {
          console.error('Error fetching quote:', err);
          // Handle error (e.g., show toast)
        }
      };

    fetchQuote();
  } else if (formData.business_id && dateTime.date && dateTime.time && formData.service_id) {
    console.log('Validating scheduling for business_id:', formData.business_id, 'date:', dateTime.date, 'time:', dateTime.time);
    validateScheduling();
  } else {
    console.log('Clearing validation state');
    setValidation(null);
  }
}, [quoteId, formData.business_id, dateTime.date, dateTime.time, formData.service_id, initialData?.id, validateScheduling]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (validation && !validation.isValid) {
      // Allow submission with warning, but user must acknowledge
      const shouldProceed = globalThis.confirm(
        `There are scheduling conflicts:\n${validation.conflicts.join('\n')}\n\nDo you still want to proceed?`
      );
      if (!shouldProceed) return;
    }

    const customer = customers.find(c => c.id === formData.customer_id);
    const service = services.find(s => s.id === formData.service_id);
    const business = businesses.find(b => b.id === formData.business_id);

    if (customer && service && business) {
      // Combine date and time into date_time
      const combinedDateTime = dateTime.date && dateTime.time 
        ? new Date(`${dateTime.date}T${dateTime.time}`).toISOString()
        : formData.date_time || '';

      const jobData: Omit<Job, 'id'> = {
        date_time: combinedDateTime,
        duration: formData.duration ?? service.duration,
        price: formData.price ?? service.basePrice,
        status: 'requested',
        isRecurring: formData.is_recurring ?? false,
        notes: formData.notes ?? '',
        recurringFrequency: formData.is_recurring ? formData.recurring_frequency : undefined,
        recurringEndDate: formData.is_recurring ? formData.recurring_end_date : undefined,
      };

      if (onSubmit) {
        onSubmit(jobData);
      } else {
        // Navigate back to jobs list if no onSubmit handler
        navigate('/jobs');
      }
    }
  }

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const serviceId = e.target.value;
    const selectedService = services.find(s => s.id === serviceId);
    setFormData(prev => ({
      ...prev,
      service_id: serviceId,
      price: selectedService?.basePrice ?? 0,
      duration: selectedService?.duration ?? 0,
    }));
  };

  const getAvailableBusinesses = () => {
    if (!dateTime.date || !dateTime.time || !formData.service_id) {
      return businesses.filter(business => business.status === 'active');
    }

    // For simplicity, show all active businesses but with availability indicators
    return businesses.filter(business => business.status === 'active');
  };

  const isBusinessAvailable = (businessId: string) => {
    if (!validation) return true;
    if (validation.isValid && formData.business_id === businessId) return true;
    
    // If there are conflicts for this business, mark as unavailable
    return !validation.conflicts.some(conflict => 
      conflict.toLowerCase().includes(businesses.find(b => b.id === businessId)?.businessName.toLowerCase() || '')
    );
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => onCancel ? onCancel() : navigate('/jobs')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {initialData ? 'Edit Job' : 'Create New Job'}
          </h1>
          <p className="text-gray-600 mt-1">
            {quoteId ? `Creating job from quote #${quoteId.slice(0, 8)}` : 'Create a new service job'}
          </p>
        </div>
      </div>

      {/* Validation Status */}
      {isValidating && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center">
          <Clock className="h-4 w-4 text-blue-600 mr-2 animate-spin" />
          <span className="text-blue-700">Checking availability...</span>
        </div>
      )}

      {validation && (
        <div className={`mb-6 p-4 rounded-xl flex items-start ${
          validation.isValid 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          {validation.isValid ? (
            <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
          )}
          <div>
            <p className={`font-medium ${validation.isValid ? 'text-green-700' : 'text-red-700'}`}>
              {validation.isValid ? 'No scheduling conflicts detected' : 'Scheduling conflicts detected'}
            </p>
            {!validation.isValid && validation.conflicts.length > 0 && (
              <ul className="text-red-600 text-sm mt-1 list-disc list-inside">
                {validation.conflicts.map((conflict, index) => (
                  <li key={index}>{conflict}</li>
                ))}
              </ul>
            )}
            {validation.suggestions.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-600">Suggested available times:</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {validation.suggestions.slice(0, 5).map((time, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setDateTime(prev => ({ ...prev, time }));
                        // Update the combined requested_date_time
                        if (dateTime.date && time) {
                          const combined = new Date(`${dateTime.date}T${time}`).toISOString();
                          setFormData(prev => ({ ...prev, requested_date_time: combined }));
                        }
                      }}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer *
              </label>
              <select
                value={formData.customer_id}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_id: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select Customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service *
              </label>
              <select
                value={formData.service_id}
                onChange={handleServiceChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select Service</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} - ${service.basePrice} ({service.duration}h)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business *
              </label>
              <select
                value={formData.business_id}
                onChange={(e) => setFormData(prev => ({ ...prev, business_id: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select Business</option>
                {getAvailableBusinesses().map(business => {
                  const available = isBusinessAvailable(business.id);
                  return (
                    <option 
                      key={business.id} 
                      value={business.id}
                      className={available ? '' : 'text-red-600'}
                    >
                      {business.businessName} {available ? '' : '(Unavailable)'}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Service address"
                required
              />
            </div>
          </div>
        </div>

        {/* Scheduling */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Scheduling</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date *
              </label>
              <input
                type="date"
                value={dateTime.date}
                onChange={(e) => {
                  setDateTime(prev => ({ ...prev, date: e.target.value }));
                  // Update the combined requested_date_time
                  if (e.target.value && dateTime.time) {
                    const combined = new Date(`${e.target.value}T${dateTime.time}`).toISOString();
                    setFormData(prev => ({ ...prev, requested_date_time: combined }));
                  }
                }}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time *
              </label>
              <input
                type="time"
                value={dateTime.time}
                onChange={(e) => {
                  setDateTime(prev => ({ ...prev, time: e.target.value }));
                  // Update the combined requested_date_time
                  if (dateTime.date && e.target.value) {
                    const combined = new Date(`${dateTime.date}T${e.target.value}`).toISOString();
                    setFormData(prev => ({ ...prev, requested_date_time: combined }));
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (hours) *
              </label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: Number.parseFloat(e.target.value) || 0 }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price ($) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: Number.parseFloat(e.target.value) || 0 }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>
        </div>

        {/* Recurring Options */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recurring Options</h2>
          
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_recurring: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">Is this a recurring job?</span>
              </label>
            </div>

            {formData.is_recurring && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency
                  </label>
                  <select
                    value={formData.recurring_frequency}
                    onChange={(e) => setFormData(prev => ({ ...prev, recurring_frequency: e.target.value as JobFormData['recurring_frequency'] }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-weekly</option>
                    <option value="tri-weekly">Tri-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.recurring_end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, recurring_end_date: e.target.value }))}
                    min={dateTime.date || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Any special instructions or notes..."
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => onCancel ? onCancel() : navigate('/jobs')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              validation && !validation.isValid
                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            {validation && !validation.isValid 
              ? 'Create Despite Conflicts' 
              : initialData 
                ? 'Update Job' 
                : 'Create Job'
            }
          </button>
        </div>
      </form>
    </div>
  );
}